// =====================================================
// CKAN Storage Helpers
// Descripción: Funciones para interactuar con datos CKAN en Supabase
// =====================================================

import { supabase } from '../supabase';
import { CKAN_CONFIG } from './ckan-config';

// Pre-import workers so Vite compiles them
import CsvWorker from '../../workers/csv-processor.worker.ts?worker';
import ExcelWorker from '../../workers/excel-processor.worker.ts?worker';
import JsonWorker from '../../workers/json-processor.worker.ts?worker';
import XmlWorker from '../../workers/xml-processor.worker.ts?worker';

// =====================================================
// TIPOS
// =====================================================

export interface CKANSyncStatus {
  id: string;
  resource_type: 'metadata' | 'csv' | 'excel' | 'json' | 'xml' | 'shapefile';
  resource_name: string;
  resource_label: string;
  status: 'idle' | 'downloading' | 'downloaded' | 'processing' | 'ready' | 'error' | 'requires_manual_upload';
  ready_to_process: boolean;
  ckan_resource_id: string | null;
  ckan_url: string | null;
  storage_path_raw: string | null;
  storage_path_processed: string | null;
  storage_strategy?: 'postgresql' | 'storage' | null;
  storage_path_data?: string | null;
  file_size_bytes: number | null;
  file_format: string | null;
  records_count: number | null;
  last_sync_started_at: string | null;
  last_sync_completed_at: string | null;
  downloaded_at: string | null;
  processed_at: string | null;
  last_error: string | null;
  error_count: number;
  sync_metadata: any;
  created_at: string;
  updated_at: string;
}

export interface AsociacionForestal {
  id: string;
  ckan_id: number;
  asociacion: string;
  domicilio: string | null;
  municipio: string;
  provincia: string;
  synced_at: string;
  created_at: string;
  updated_at: string;
}

export interface DemografiaCyL {
  id: string;
  resource_name: string;
  ckan_resource_id: string;
  provincia: string | null;
  anio: number | null;
  valor: number | null;
  valor_hombres: number | null;
  valor_mujeres: number | null;
  metadata: any;
  synced_at: string;
  created_at: string;
  updated_at: string;
}

// =====================================================
// FUNCIONES: SYNC STATUS
// =====================================================

/**
 * Obtener todos los estados de sincronización
 */
export async function getAllSyncStatus(): Promise<CKANSyncStatus[]> {
  const { data, error } = await supabase
    .from('ckan_sync_status')
    .select('*')
    .order('resource_type', { ascending: true })
    .order('resource_name', { ascending: true });

  if (error) throw error;
  return (data as CKANSyncStatus[]) || [];
}

/**
 * Obtener estado de un recurso específico
 */
export async function getSyncStatus(resourceName: string): Promise<CKANSyncStatus | null> {
  const { data, error } = await supabase
    .from('ckan_sync_status')
    .select('*')
    .eq('resource_name', resourceName)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data as CKANSyncStatus;
}

/**
 * Actualizar estado de sincronización
 */
export async function updateSyncStatus(
  resourceName: string,
  updates: Partial<CKANSyncStatus>
): Promise<CKANSyncStatus> {

  const exists = await getSyncStatus(resourceName);

  console.log("exists ? ", exists)

  console.log("resourceName", resourceName);
  console.log("updates", updates);
  const { data, error } = await (supabase
    .from('ckan_sync_status') as any)
    .update(updates)
    .eq('resource_name', resourceName)
    .select()
    //.single();

  if (error) throw error;
  return data as CKANSyncStatus;
}

// =====================================================
// FUNCIONES: EDGE FUNCTIONS
// =====================================================

/**
 * Sincronizar metadatos de CKAN
 */
export async function syncCKANMetadata() {
  const { data, error } = await supabase.functions.invoke('sync-ckan-metadata', {
    method: 'POST',
  });

  if (error) throw error;
  return data;
}

/**
 * Sincronizar CSV (asociaciones o demografía)
 */
export async function syncCKANCSV(resourceName: string) {
  const { data, error } = await supabase.functions.invoke('sync-ckan-csv', {
    method: 'POST',
    body: { resource_name: resourceName },
  });

  if (error) throw error;
  return data;
}

/**
 * Sincronizar Excel
 */
export async function syncCKANExcel(resourceName: string) {
  const { data, error } = await supabase.functions.invoke('sync-ckan-excel', {
    method: 'POST',
    body: { resource_name: resourceName },
  });

  if (error) throw error;
  return data;
}

/**
 * Sincronizar JSON
 */
export async function syncCKANJSON(resourceName: string) {
  const { data, error } = await supabase.functions.invoke('sync-ckan-json', {
    method: 'POST',
    body: { resource_name: resourceName },
  });

  if (error) throw error;
  return data;
}

/**
 * Sincronizar XML
 */
export async function syncCKANXML(resourceName: string) {
  const { data, error } = await supabase.functions.invoke('sync-ckan-xml', {
    method: 'POST',
    body: { resource_name: resourceName },
  });

  if (error) throw error;
  return data;
}

/**
 * Sincronizar archivo de datos (CSV, Excel, JSON, XML)
 * Elige automáticamente la Edge Function correcta según el tipo
 */
export async function syncDataFile(resourceName: string, fileType: 'csv' | 'excel' | 'json' | 'xml') {
  switch (fileType) {
    case 'csv':
      return syncCKANCSV(resourceName);
    case 'excel':
      return syncCKANExcel(resourceName);
    case 'json':
      return syncCKANJSON(resourceName);
    case 'xml':
      return syncCKANXML(resourceName);
    default:
      throw new Error(`Unsupported file type: ${fileType}`);
  }
}

/**
 * Descargar Shapefile a Storage
 */
export async function downloadShapefile(resourceName: string) {
  const { data, error } = await supabase.functions.invoke('sync-ckan-shapefiles', {
    method: 'POST',
    body: { resource_name: resourceName },
  });

  

  if (error) throw error;
  return data;
}

/**
 * Subir shapefile manualmente (para archivos grandes >150MB)
 */
export async function uploadShapefileManually(
  resourceName: string,
  file: File,
  onProgress?: (message: string) => void
): Promise<{ success: boolean; path: string }> {
  try {
    onProgress?.(`Validando archivo (${(file.size / 1024 / 1024).toFixed(2)} MB)...`);

    // Validar que sea un ZIP
    if (!file.name.endsWith('.zip') && file.type !== 'application/zip') {
      throw new Error('El archivo debe ser un ZIP');
    }

    // Validar firma ZIP
    const header = await file.slice(0, 4).arrayBuffer();
    const headerBytes = new Uint8Array(header);
    const isPKZip = headerBytes[0] === 0x50 && headerBytes[1] === 0x4B;

    if (!isPKZip) {
      throw new Error('El archivo no es un ZIP válido');
    }

    onProgress?.('Subiendo archivo a Storage...');

    // Upload al PHP endpoint
    const dashboardUrl = import.meta.env.VITE_DASHBOARD_URL || 'https://monbiofor.cesefor.me';
    const hostingApiKey = '6868e4727e5ca04cc19a5cc7ff21adc8c516d0156eca6b558fba5d00a0f7e20f';

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const fileName = `${resourceName}-${timestamp}.zip`;

    const formData = new FormData();
    formData.append('file', file, fileName);
    formData.append('folder', 'raw');
    formData.append('filename', fileName);

    const response = await fetch(`${dashboardUrl}/api/upload-ckan-file.php`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${hostingApiKey}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${errorText}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Upload failed');
    }

    onProgress?.('Actualizando estado en base de datos...');

    // Obtener sync status actual para preservar metadata
    const currentStatus = await getSyncStatus(resourceName);
    const currentMetadata = currentStatus?.sync_metadata || {};

    // Actualizar sync_status
    const { error: updateError } = await (supabase
      .from('ckan_sync_status') as any)
      .update({
        status: 'downloaded',
        storage_path_raw: result.path,
        ready_to_process: true,
        file_size_bytes: file.size,
        downloaded_at: new Date().toISOString(),
        sync_metadata: {
          ...currentMetadata,
          manually_uploaded: true,
          upload_timestamp: new Date().toISOString(),
        },
      })
      .eq('resource_name', resourceName);

    if (updateError) throw updateError;

    onProgress?.('¡Archivo subido exitosamente!');

    return {
      success: true,
      path: result.path,
    };
  } catch (error) {
    throw error instanceof Error ? error : new Error('Upload failed');
  }
}

/**
 * Sincronizar todos los recursos CKAN
 */
export async function syncAllCKAN(types: string[] = ['csv']) {
  const { data, error } = await supabase.functions.invoke('sync-all-ckan', {
    method: 'POST',
    body: { types },
  });

  if (error) throw error;
  return data;
}

/**
 * Descubrir TODOS los recursos desde CKAN API (escalable a N recursos)
 * Usa Edge Function para evitar CORS
 */
/**
 * Descubrir recursos CKAN
 * Soporta 2 modos:
 * - 'edge-functions': Llama a Edge Function (CKAN debe ser público)
 * - 'frontend': Llama directamente desde el navegador (CKAN puede ser privado si estás en la misma red)
 */
export async function discoverCKANResources() {
  if (CKAN_CONFIG.MODE === 'frontend') {
    // MODO FRONTEND: Llamar directamente a CKAN desde el navegador
    return await discoverCKANResourcesFrontend();
  } else {
    // MODO EDGE FUNCTIONS: Llamar a la Edge Function
    const { data, error } = await supabase.functions.invoke('discover-ckan', {
      method: 'POST',
    });

    if (error) throw error;
    return data;
  }
}

/**
 * Descubrir recursos llamando directamente a CKAN desde el frontend
 * SOLO USAR SI CKAN NO ES ACCESIBLE DESDE EDGE FUNCTIONS
 */
async function discoverCKANResourcesFrontend() {
  console.log('[Frontend Mode] Discovering CKAN resources...');

  try {
    // 1. Llamar a CKAN API directamente
    const response = await fetch(
      `${CKAN_CONFIG.CKAN_API_URL}/api/3/action/package_search?q=*:*&rows=1000`,
      {
        headers: {
          'Authorization': `Bearer ${CKAN_CONFIG.CKAN_API_TOKEN}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`CKAN API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error('CKAN API returned success: false');
    }

    const datasets = data.result.results;
    console.log(`Found ${datasets.length} datasets`);

    // 2. Extraer recursos
    const resources: any[] = [];
    let csvCount = 0;
    let shapefileCount = 0;
    let otherCount = 0;

    for (const dataset of datasets) {
      if (!dataset.resources || dataset.resources.length === 0) continue;

      const datasetTitle = dataset.title || dataset.name || 'Unknown Dataset';

      for (const resource of dataset.resources) {
        // Auto-detectar tipo DINÁMICAMENTE (CSV, XLS, JSON, XML, Shapefile)
        const format = (resource.format || '').toLowerCase();
        let resourceType: 'csv' | 'excel' | 'json' | 'xml' | 'shapefile' | 'metadata' = 'metadata';

        if (format.includes('csv')) {
          resourceType = 'csv';
          csvCount++;
        } else if (format.includes('xls') || format.includes('xlsx')) {
          resourceType = 'excel';
          csvCount++; // Contar Excel como "data files"
        } else if (format.includes('json')) {
          resourceType = 'json';
          csvCount++; // Contar JSON como "data files"
        } else if (format.includes('xml')) {
          resourceType = 'xml';
          csvCount++; // Contar XML como "data files"
        } else if (format.includes('shp') || format.includes('zip')) {
          resourceType = 'shapefile';
          shapefileCount++;
        } else {
          otherCount++;
        }

        // Slug único
        const slug = (resource.name || resource.description || `${datasetTitle}_${resource.id}`)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_|_$/g, '');

        resources.push({
          resource_type: resourceType,
          resource_name: slug,
          resource_label: resource.name || resource.description || datasetTitle,
          ckan_resource_id: resource.id,
          ckan_url: resource.url,
          file_format: resource.format || null,
          file_size_bytes: resource.size || null,
          sync_metadata: {
            dataset_title: datasetTitle,
            dataset_name: dataset.name,
            resource_description: resource.description,
            last_modified: resource.last_modified || resource.created || null,
            discovered_from: 'frontend',
          },
        });
      }
    }

    console.log(`Total: ${resources.length} resources (Data files: ${csvCount}, Shapefiles: ${shapefileCount}, Other: ${otherCount})`);

    // 3. Verificar cuáles ya existen
    const { data: existing } = await supabase
      .from('ckan_sync_status')
      .select('resource_name');

    const existingNames = new Set((existing || []).map((r: any) => r.resource_name));

    // 4. Filtrar solo nuevos
    const newResources = resources.filter(r => !existingNames.has(r.resource_name));

    console.log(`New resources: ${newResources.length}`);

    if (newResources.length === 0) {
      return {
        success: true,
        message: 'All resources already in database',
        stats: {
          total: resources.length,
          new: 0,
          existing: existingNames.size,
          data_files: csvCount,
          shapefiles: shapefileCount,
          other: otherCount,
        },
      };
    }

    // 5. Insertar nuevos recursos
    const { error: insertError } = await supabase
      .from('ckan_sync_status')
      .insert(newResources as any);

    if (insertError) {
      throw new Error(`Database insert error: ${insertError.message}`);
    }

    return {
      success: true,
      message: `Discovered ${newResources.length} new resources`,
      stats: {
        total: resources.length,
        new: newResources.length,
        existing: existingNames.size,
        data_files: csvCount,
        shapefiles: shapefileCount,
        other: otherCount,
      },
    };

  } catch (error: any) {
    console.error('[Frontend Mode] Error:', error);
    throw error;
  }
}

// =====================================================
// FUNCIONES: STORAGE
// =====================================================

/**
 * Descargar archivo desde Hosting Storage como Blob
 */
export async function downloadFromStorage(path: string): Promise<Blob> {
  const dashboardUrl = import.meta.env.VITE_DASHBOARD_URL || 'https://monbiofor.cesefor.me';

  // Si el path ya incluye /ckan/, no lo agregamos de nuevo
  const normalizedPath = path.startsWith('/ckan/') ? path.substring(1) : path;
  const url = `${dashboardUrl}/${normalizedPath}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Download failed (${response.status}): ${response.statusText}`);
  }

  return await response.blob();
}

/**
 * Subir GeoJSON procesado a Storage (legacy - usar uploadGeoJSONSmart)
 * @deprecated Use uploadGeoJSONSmart() instead
 */
export async function uploadGeoJSON(
  resourceName: string,
  geojson: any
): Promise<string> {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const fileName = `${resourceName}-${timestamp}.geojson`;
  const path = `geojson/${fileName}`;

  const { error } = await supabase.storage
    .from('ckan')
    .upload(path, JSON.stringify(geojson), {
      contentType: 'application/geo+json',
      upsert: false,
    });

  if (error) throw error;
  return path;
}

// =====================================================
// GEOJSON CHUNKING - Manejo automático de archivos grandes
// =====================================================

import { GEOJSON_CONFIG, type ChunkInfo, type UploadResult } from './geojson-config';

/**
 * Calcular tamaño óptimo de chunk basado en el tamaño total
 */
function autoCalculateChunkSize(totalFeatures: number, geojsonSizeBytes: number): number {
  const sizeInMB = geojsonSizeBytes / 1024 / 1024;

  // Calcular cuántos chunks necesitamos basado en el tamaño objetivo
  const estimatedChunks = Math.ceil(sizeInMB / 10); // ~10 MB por chunk
  let featuresPerChunk = Math.ceil(totalFeatures / estimatedChunks);

  // Aplicar límites
  featuresPerChunk = Math.max(GEOJSON_CONFIG.MIN_FEATURES_PER_CHUNK, featuresPerChunk);
  featuresPerChunk = Math.min(GEOJSON_CONFIG.MAX_FEATURES_PER_CHUNK, featuresPerChunk);

  console.log(`  Calculating optimal chunk size... ${featuresPerChunk} features per chunk`);

  return featuresPerChunk;
}

/**
 * Subir GeoJSON como archivo único (< 30 MB) al Hosting Storage
 */
async function uploadGeoJSONSingle(
  resourceName: string,
  geojsonString: string
): Promise<string> {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const fileName = `${resourceName}-${timestamp}.geojson`;

  const sizeInMB = (geojsonString.length / 1024 / 1024).toFixed(2);
  console.log(`📦 Uploading as single file (${sizeInMB} MB)`);

  // Convertir string a Blob para el upload
  const blob = new Blob([geojsonString], { type: 'application/json' });

  // Upload al Hosting Storage (PHP en Ferozo)
  const dashboardUrl = import.meta.env.VITE_DASHBOARD_URL || 'https://monbiofor.cesefor.me';
  const hostingApiKey = '6868e4727e5ca04cc19a5cc7ff21adc8c516d0156eca6b558fba5d00a0f7e20f';
  const formData = new FormData();
  formData.append('file', blob, fileName);
  formData.append('folder', 'geojson');
  formData.append('filename', fileName);
  console.log("dashboardUrl", dashboardUrl);

  const response = await fetch(`${dashboardUrl}/api/upload-ckan-file.php`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${hostingApiKey}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Upload error:', errorText);
    throw new Error(
      `Failed to upload GeoJSON to Hosting Storage (${response.status}): ${errorText}`
    );
  }

  const result = await response.json();

  if (!result.success) {
    console.error('Upload uploadGeoJSONSingle:', result );
    throw new Error(`Upload failed: ${result.error || 'Unknown error'}`);
  }

  console.log(`✅ Uploaded successfully: ${result.url}`);
  return result.path;
}

/**
 * Subir GeoJSON dividido en chunks (>= 30 MB) al Hosting Storage
 */
async function uploadGeoJSONChunked(
  resourceName: string,
  geojson: any,
  featuresPerChunk: number,
  onProgress?: (message: string) => void
): Promise<{ paths: string[]; chunkInfo: ChunkInfo[] }> {
  const features = geojson.features || [];
  const totalChunks = Math.ceil(features.length / featuresPerChunk);
  const uploadedPaths: string[] = [];
  const chunkInfoList: ChunkInfo[] = [];

  const splittingMessage = `🔪 Splitting ${features.length} features into ${totalChunks} chunks`;
  console.log(splittingMessage);
  onProgress?.(splittingMessage);

  const dashboardUrl = import.meta.env.VITE_DASHBOARD_URL || 'https://monbiofor.cesefor.me';

  for (let i = 0; i < totalChunks; i++) {
    const start = i * featuresPerChunk;
    const end = Math.min(start + featuresPerChunk, features.length);
    const chunkFeatures = features.slice(start, end);

    const chunk = {
      type: 'FeatureCollection',
      features: chunkFeatures,
    };

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const fileName = `${resourceName}_part${i + 1}_of_${totalChunks}-${timestamp}.geojson`;

    const chunkString = JSON.stringify(chunk);
    const sizeInMB = (chunkString.length / 1024 / 1024).toFixed(2);

    const uploadMessage = `📤 Uploading part ${i + 1}/${totalChunks} (${sizeInMB} MB, ${chunkFeatures.length} features)`;
    console.log(uploadMessage);
    onProgress?.(uploadMessage);

    // Convertir string a Blob para el upload
    const chunkBlob = new Blob([chunkString], { type: 'application/json' });

    // Upload al Hosting Storage (PHP en Ferozo)
    const hostingApiKey = '6868e4727e5ca04cc19a5cc7ff21adc8c516d0156eca6b558fba5d00a0f7e20f';
    const formData = new FormData();
    formData.append('file', chunkBlob, fileName);
    formData.append('folder', 'geojson');
    formData.append('filename', fileName);

    const response = await fetch(`${dashboardUrl}/api/upload-ckan-file.php`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${hostingApiKey}`,
      },
      body: formData,
    });

    if (!response.ok) {
      console.log("error uploadGeoJSONChunked", response);
      const errorText = await response.text();
      throw new Error(`Upload chunk ${i + 1} failed (${response.status}): ${errorText}`);
    }

    const result = await response.json();

    if (!result.success) {
      console.log("error uploadGeoJSONChunked2", result);
      throw new Error(`Upload chunk ${i + 1} failed: ${result.error || 'Unknown error'}`);
    }

    uploadedPaths.push(result.path);
    chunkInfoList.push({
      path: result.path,
      features: chunkFeatures.length,
      size_mb: parseFloat(sizeInMB),
      index: i + 1,
    });
  }

  const successMessage = `✅ Uploaded ${totalChunks} chunks successfully`;
  console.log(successMessage);
  onProgress?.(successMessage);

  return { paths: uploadedPaths, chunkInfo: chunkInfoList };
}

/**
 * Subir GeoJSON con chunking automático (Smart Upload)
 * - Archivos < 30 MB: Un solo archivo
 * - Archivos 30-500 MB: Chunking automático
 * - Archivos > 500 MB: Error
 */
export async function uploadGeoJSONSmart(
  resourceName: string,
  geojson: any,
  onProgress?: (message: string) => void
): Promise<UploadResult> {
  const geojsonString = JSON.stringify(geojson);
  const sizeInBytes = geojsonString.length;
  const sizeInMB = sizeInBytes / 1024 / 1024;
  const totalFeatures = geojson.features?.length || 0;

  const message = `📊 GeoJSON: ${totalFeatures.toLocaleString()} features, ${sizeInMB.toFixed(2)} MB`;
  console.log(message);
  onProgress?.(message);

  // Verificar límite máximo (500 MB)
  if (sizeInBytes > GEOJSON_CONFIG.MAX_TOTAL_SIZE) {
    const maxSizeMB = (GEOJSON_CONFIG.MAX_TOTAL_SIZE / 1024 / 1024).toFixed(0);
    throw new Error(
      `GeoJSON file is too large (${sizeInMB.toFixed(2)} MB). ` +
      `Maximum allowed is ${maxSizeMB} MB. Consider simplifying the geometry or ` +
      `using a smaller dataset.`
    );
  }

  // Decidir estrategia: single file o chunking
  if (sizeInBytes <= GEOJSON_CONFIG.MAX_SINGLE_FILE_SIZE) {
    // Archivo pequeño: subir como un solo archivo
    const path = await uploadGeoJSONSingle(resourceName, geojsonString);
    return {
      isChunked: false,
      paths: [path],
      totalSize: sizeInBytes,
      totalFeatures,
    };
  } else {
    // Archivo grande: chunking automático
    const chunkMessage = `🔪 File too large, splitting into chunks`;
    console.log(chunkMessage);
    onProgress?.(chunkMessage);

    const chunkSize = autoCalculateChunkSize(totalFeatures, sizeInBytes);
    const chunkCalcMessage = `Calculating optimal chunk size... ${chunkSize} features per chunk`;
    console.log(chunkCalcMessage);
    onProgress?.(chunkCalcMessage);

    const result = await uploadGeoJSONChunked(resourceName, geojson, chunkSize, onProgress);

    return {
      isChunked: true,
      paths: result.paths,
      totalSize: sizeInBytes,
      totalFeatures,
      chunkInfo: result.chunkInfo,
    };
  }
}

/**
 * Obtener URL pública de un archivo en Hosting Storage
 */
export function getPublicURL(path: string): string {
  const dashboardUrl = import.meta.env.VITE_DASHBOARD_URL || 'https://monbiofor.cesefor.me';

  // Si el path ya incluye /ckan/, no lo agregamos de nuevo
  const normalizedPath = path.startsWith('/ckan/') ? path.substring(1) : path;
  return `${dashboardUrl}/${normalizedPath}`;
}

// =====================================================
// FUNCIONES: DATOS
// =====================================================

/**
 * Obtener todas las asociaciones forestales
 */
export async function getAsociacionesForestales(filters?: {
  provincia?: string;
  municipio?: string;
  search?: string;
}): Promise<AsociacionForestal[]> {
  let query = supabase
    .from('ckan_asociaciones_forestales')
    .select('*')
    .order('asociacion', { ascending: true });

  if (filters?.provincia) {
    query = query.eq('provincia', filters.provincia);
  }

  if (filters?.municipio) {
    query = query.eq('municipio', filters.municipio);
  }

  if (filters?.search) {
    query = query.ilike('asociacion', `%${filters.search}%`);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

/**
 * Obtener datos demográficos con filtros
 */
export async function getDemografiaCyL(filters?: {
  resource_name?: string;
  provincia?: string;
  anio?: number;
}): Promise<DemografiaCyL[]> {
  let query = supabase
    .from('ckan_demografia_cyl')
    .select('*')
    .order('anio', { ascending: false });

  if (filters?.resource_name) {
    query = query.eq('resource_name', filters.resource_name);
  }

  if (filters?.provincia) {
    query = query.eq('provincia', filters.provincia);
  }

  if (filters?.anio) {
    query = query.eq('anio', filters.anio);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

/**
 * Obtener lista de provincias únicas
 */
export async function getProvincias(): Promise<string[]> {
  const { data, error } = await supabase
    .from('ckan_asociaciones_forestales')
    .select('provincia')
    .order('provincia', { ascending: true });

  if (error) throw error;

  const provincias = [...new Set((data as any).map((d: any) => d.provincia))] as string[];
  return provincias.filter(p => p) as string[]; // Remover nulls
}

/**
 * Obtener lista de municipios por provincia
 */
export async function getMunicipios(provincia?: string): Promise<string[]> {
  let query = supabase
    .from('ckan_asociaciones_forestales')
    .select('municipio')
    .order('municipio', { ascending: true });

  if (provincia) {
    query = query.eq('provincia', provincia);
  }

  const { data, error } = await query;

  if (error) throw error;

  const municipios = [...new Set((data as any).map((d: any) => d.municipio))] as string[];
  return municipios.filter(m => m) as string[]; // Remover nulls
}

// =====================================================
// FUNCIONES: PROCESAMIENTO DE SHAPEFILES
// =====================================================

/**
 * Procesar Shapefile descargado (usando Web Worker)
 */
export async function processShapefile(
  resourceName: string,
  onProgress?: (status: string) => void
): Promise<{ geojson: any; metadata: any }> {
  try {
    // 1. Obtener info del recurso
    const syncStatus = await getSyncStatus(resourceName);

    if (!syncStatus) {
      throw new Error(
        `❌ Resource "${resourceName}" not found in database. ` +
        `Please run "Discover CKAN Resources" first to populate the database.`
      );
    }

    // Verificar que haya un archivo para procesar
    if (!syncStatus.storage_path_raw) {
      throw new Error('Shapefile not downloaded. Please download it first using "Sync Shapefile".');
    }

    if (!syncStatus.ready_to_process) {
      throw new Error('Shapefile not ready to process');
    }

    // 2. Descargar ZIP desde Storage
    onProgress?.('Downloading shapefile from storage...');
    let zipBlob: Blob;

    try {

      console.log("syncStatus.storage_path_raw", syncStatus.storage_path_raw);
      zipBlob = await downloadFromStorage(syncStatus.storage_path_raw);

      // Validar que es un archivo ZIP válido
      if (zipBlob.size < 100) {
        throw new Error(
          `❌ El archivo ZIP descargado está vacío o corrupto (${zipBlob.size} bytes). ` +
          `Esto puede deberse a un error durante la descarga. ` +
          `Intenta sincronizar el recurso nuevamente.`
        );
      }

      // Validar firma ZIP leyendo los primeros bytes
      const header = await zipBlob.slice(0, 4).arrayBuffer();
      const headerBytes = new Uint8Array(header);
      const isPKZip = headerBytes[0] === 0x50 && headerBytes[1] === 0x4B; // "PK"

      if (!isPKZip) {
        throw new Error(
          `❌ El archivo descargado no es un ZIP válido. ` +
          `Es posible que se haya descargado una página HTML de error en lugar del archivo. ` +
          `Por favor, sincroniza el recurso nuevamente desde CKAN.`
        );
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('Download failed (404)')) {
        throw new Error(
          `❌ El archivo ZIP no se encontró en el servidor (404). ` +
          `Ruta: ${syncStatus.storage_path_raw}. ` +
          `Intenta sincronizar el recurso nuevamente.`
        );
      }
      throw error;
    }

    onProgress?.('Processing shapefile...');

    // 3. Crear Worker
    const worker = new Worker(
      new URL('../../workers/shapefile-converter.worker.ts', import.meta.url),
      { type: 'module' }
    );

    // 4. Enviar al worker y esperar respuesta
    const result = await new Promise<{ geojson: any; metadata: any }>((resolve, reject) => {
      worker.onmessage = (event) => {
        const response = event.data;

        // Handle progress messages - don't resolve/reject, just pass to callback
        if (response.type === 'progress' && response.log) {
          onProgress?.(response.log);
          return; // Continue waiting for completion
        }

        // Handle completion message
        if (response.type === 'complete' && response.success) {
          resolve({
            geojson: response.geojson,
            metadata: response.metadata,
          });
          worker.terminate();
          return;
        }

        // Handle error messages or legacy success check
        if (!response.success || response.type === 'error') {
          // Mejorar mensajes de error del Worker
          let errorMsg = response.error || 'Worker processing failed';

          if (errorMsg.includes("Can't find end of central directory")) {
            errorMsg = `❌ El archivo ZIP está corrupto o incompleto. ` +
                      `Esto puede deberse a:\n` +
                      `• Error durante la descarga desde CKAN\n` +
                      `• El archivo está corrupto en el servidor CKAN\n` +
                      `• Problema de red durante la transferencia\n\n` +
                      `Solución: Intenta sincronizar el recurso nuevamente.`;
          } else if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError')) {
            errorMsg = `❌ Error de red al procesar el shapefile. ` +
                      `Verifica tu conexión a internet e intenta nuevamente.`;
          } else if (errorMsg.includes('quota') || errorMsg.includes('memory')) {
            errorMsg = `❌ No hay suficiente memoria para procesar este shapefile. ` +
                      `El archivo puede ser demasiado grande para procesarse en el navegador.`;
          }

          reject(new Error(errorMsg));
          worker.terminate();
        }
      };

      worker.onerror = (error) => {
        const errorMsg = `❌ Error inesperado al procesar shapefile: ${error.message || 'Unknown error'}. ` +
                        `Por favor, reporta este error.`;
        reject(new Error(errorMsg));
        worker.terminate();
      };

      worker.postMessage({ zipBlob, resourceName, reprojectToWGS84: true });
    });

    onProgress?.('Extracting GeoJSON metadata...');

    // 5. Extraer metadata del GeoJSON
    const geojson = result.geojson;
    const features = geojson.features || [];

    // Extraer propiedades (columnas) del primer feature
    const properties: string[] = [];
    const sampleData: any[] = [];
    if (features.length > 0 && features[0].properties) {
      Object.keys(features[0].properties).forEach(key => {
        properties.push(key);
      });

      // Tomar hasta 3 features como muestra (consistente con CSVs)
      sampleData.push(...features.slice(0, 3).map((f: any) => f.properties));
    }

    // Detectar tipos de geometría
    const geometryTypes = new Set<string>();
    features.forEach((feature: any) => {
      if (feature.geometry && feature.geometry.type) {
        geometryTypes.add(feature.geometry.type);
      }
    });

    // Calcular bounds (bounding box)
    let bounds: [number, number, number, number] | null = null;
    if (features.length > 0) {
      let minLng = Infinity, minLat = Infinity;
      let maxLng = -Infinity, maxLat = -Infinity;

      features.forEach((feature: any) => {
        if (feature.geometry && feature.geometry.coordinates) {
          const extractCoords = (coords: any): void => {
            if (Array.isArray(coords)) {
              if (typeof coords[0] === 'number') {
                // Es un punto [lng, lat]
                minLng = Math.min(minLng, coords[0]);
                maxLng = Math.max(maxLng, coords[0]);
                minLat = Math.min(minLat, coords[1]);
                maxLat = Math.max(maxLat, coords[1]);
              } else {
                // Es un array de coordenadas
                coords.forEach(extractCoords);
              }
            }
          };
          extractCoords(feature.geometry.coordinates);
        }
      });

      if (minLng !== Infinity) {
        bounds = [minLng, minLat, maxLng, maxLat];
      }
    }

    onProgress?.('Uploading GeoJSON to Storage...');

    // 6. Subir GeoJSON a Storage con chunking automático
    const uploadResult = await uploadGeoJSONSmart(resourceName, geojson, onProgress);

    // 7. Actualizar sync status con metadata completa
    const storagePathProcessed = uploadResult.isChunked
      ? uploadResult.paths[0] // Primera parte como referencia principal
      : uploadResult.paths[0];

      

    await updateSyncStatus(resourceName, {
      status: 'ready',
      ready_to_process: false, // Ya procesado
      storage_path_processed: storagePathProcessed,
      processed_at: new Date().toISOString(),
      records_count: result.metadata.features_count,
      headers: properties, // Columnas disponibles
      sample_data: sampleData, // Array de registros de ejemplo (consistente con CSV)
      geometry_types: Array.from(geometryTypes),
      bounds: bounds,
      sync_metadata: {
        ...syncStatus.sync_metadata,
        geojson_path: storagePathProcessed,
        geojson_chunked: uploadResult.isChunked,
        geojson_chunks: uploadResult.isChunked ? uploadResult.paths : undefined,
        geojson_chunk_info: uploadResult.chunkInfo,
        geojson_total_size_mb: (uploadResult.totalSize / 1024 / 1024).toFixed(2),
        features_count: result.metadata.features_count,
        processing_time_ms: result.metadata.processing_time_ms,
        processed_timestamp: new Date().toISOString(),
      },
    } as Partial<CKANSyncStatus>);

    onProgress?.('Processing complete!');

    return result;

  } catch (error) {
    console.log("error sync");
    // Intentar actualizar con error (solo si el recurso existe)
    try {
      const exists = await getSyncStatus(resourceName);
      if (exists) {
        await updateSyncStatus(resourceName, {
          status: 'error',
          last_error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    } catch (updateError) {
      console.error('Failed to update error status:', updateError);
      // No lanzar error aquí, el error principal ya se lanzará abajo
    }

    throw error;
  }
}

/**
 * Cargar GeoJSON desde Storage (si ya está procesado)
 */
export async function loadGeoJSON(resourceName: string): Promise<any> {
  const syncStatus = await getSyncStatus(resourceName);

  if (!syncStatus || !syncStatus.storage_path_processed) {
    throw new Error('GeoJSON not available. Process shapefile first.');
  }

  const url = getPublicURL(syncStatus.storage_path_processed);

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to load GeoJSON: ${response.statusText}`);
  }

  return await response.json();
}

// =====================================================
// FUNCIONES: PROCESAMIENTO DE ARCHIVOS DE DATOS (CSV/Excel/JSON/XML) EN BROWSER
// =====================================================

export interface ProcessingResult {
  records_count: number;
  storage_strategy: 'postgresql' | 'storage';
  storage_path_data?: string;
}

/**
 * Procesar archivo de datos descargado (CSV, Excel, JSON, XML) usando Web Worker
 */
export async function processDataFileInBrowser(
  resourceName: string,
  fileType: 'csv' | 'excel' | 'json' | 'xml',
  onProgress?: (status: string, progress?: number) => void
): Promise<ProcessingResult> {
  try {
    // 1. Obtener info del recurso
    const syncStatus = await getSyncStatus(resourceName);

    if (!syncStatus) {
      throw new Error(
        `❌ Resource "${resourceName}" not found in database. ` +
        `Please run "Discover CKAN Resources" first to populate the database.`
      );
    }

    if (!syncStatus.storage_path_raw) {
      throw new Error(`File not downloaded. Please sync it first.`);
    }

    if (!syncStatus.ready_to_process) {
      throw new Error('File not ready to process');
    }

    onProgress?.('Downloading file from Storage...', 10);

    // 2. Descargar archivo desde Storage
    const fileBlob = await downloadFromStorage(syncStatus.storage_path_raw);

    onProgress?.('Starting browser processing...', 20);

    // 3. Crear Worker apropiado según tipo de archivo
    const worker = createWorkerByType(fileType);

    // 4. Enviar al worker y esperar respuesta
    const result = await new Promise<ProcessingResult>((resolve, reject) => {
      worker.onmessage = (event) => {
        const response = event.data;

        if (response.type === 'progress') {
          onProgress?.(response.status, response.progress);
        } else if (response.type === 'success') {
          resolve(response.result);
          worker.terminate();
        } else if (response.type === 'error') {
          reject(new Error(response.error));
          worker.terminate();
        }
      };

      worker.onerror = (error) => {
        reject(error);
        worker.terminate();
      };

      // Enviar mensaje al worker
      const message: any = {
        type: 'process',
        resourceName,
        supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
        supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      };

      // Set the appropriate blob property based on file type
      switch (fileType) {
        case 'csv':
          message.csvBlob = fileBlob;
          break;
        case 'excel':
          message.excelBlob = fileBlob;
          break;
        case 'json':
          message.jsonBlob = fileBlob;
          break;
        case 'xml':
          message.xmlBlob = fileBlob;
          break;
      }

      worker.postMessage(message);
    });

    onProgress?.('Processing complete!', 100);

    return result;

  } catch (error) {
    // Intentar actualizar con error
    try {
      const exists = await getSyncStatus(resourceName);
      if (exists) {
        await updateSyncStatus(resourceName, {
          status: 'error',
          last_error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    } catch (updateError) {
      console.error('Failed to update error status:', updateError);
    }

    throw error;
  }
}

/**
 * Helper: Create worker instance based on file type
 */
function createWorkerByType(fileType: 'csv' | 'excel' | 'json' | 'xml'): Worker {
  switch (fileType) {
    case 'csv':
      return new CsvWorker();
    case 'excel':
      return new ExcelWorker();
    case 'json':
      return new JsonWorker();
    case 'xml':
      return new XmlWorker();
    default:
      throw new Error(`Unknown file type: ${fileType}`);
  }
}
